import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from './axiosInstance';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage } from '../utils/apiError';

const Verification = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email') || '';
  const firstName = queryParams.get('first_name') || '';
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

  const inputRefs = useRef([]);

  // Fetch and store CSRF token when the component mounts
  useEffect(() => {
    const fetchCSRFToken = async () => {
      try {
        const response = await axiosInstance.get('csrf/');
        axiosInstance.defaults.headers.common['X-CSRFToken'] = response.data.csrf_token;
        console.log('CSRF token set:', response.data.csrf_token);
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };

    fetchCSRFToken();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const enteredCode = code.join('');
    if (enteredCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    try {
      const response = await axiosInstance.post('verification/', {
        email,
        code: enteredCode,
      });

      if (response.data.message) {
        setSuccess(response.data.message);
        // Redirect to login page after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      console.error('Error:', error);
      setError(getApiErrorMessage(error, 'Verification failed. Please try again.'));
    }
  };

  const sendVerificationEmail = async () => {
    setIsResending(true);
    setError('');
    setSuccess('');
    
    try {
      // Generate new verification code
      const newVerificationCode = Math.floor(100000 + Math.random() * 900000).toString();
      
      const response = await axiosInstance.post('send-verification-email/', {
        email,
        firstName,
        verificationCode: newVerificationCode,
      });

      console.log('Verification email sent successfully', response.data);
      setSuccess('Verification email has been resent! Please check your inbox. The code expires in 30 minutes.');
      // Clear the input fields
      setCode(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError(getApiErrorMessage(error, 'Failed to resend verification email. Please try again.'));
    } finally {
      setIsResending(false);
    }
  };

  const handleChange = (e, index) => {
    const newCode = [...code];
    newCode[index] = e.target.value;
    setCode(newCode);

    // Move focus to next input if the current input has a value
    if (e.target.value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-6 bg-white rounded-md shadow-md">
        <h2 className="mb-6 text-2xl font-semibold">Verification</h2>
        {error && (
          <div className="mb-4 p-4 text-red-700 bg-red-100 border-l-4 border-red-500 rounded whitespace-pre-line">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 text-green-700 bg-green-100 border-l-4 border-green-500 rounded">
            {success}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="code" className="block mb-2 text-sm font-bold text-gray-700">
              Enter the 6-digit verification code sent to your email
            </label>
            <div className="flex">
              {code.map((num, index) => (
                <input
                  key={index}
                  type="text"
                  id={`code-${index}`}
                  className="w-full p-2 mr-2 border rounded-md"
                  placeholder="0"
                  maxLength="1"
                  value={num}
                  onChange={(e) => handleChange(e, index)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  required
                />
              ))}
            </div>
          </div>
          <button
            type="submit"
            className="w-full p-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:shadow-outline-blue"
          >
            Verify
          </button>
        </form>
        <button
          type="button"
          onClick={sendVerificationEmail}
          disabled={isResending}
          className="w-full p-2 mt-4 text-white bg-[#59844F] rounded-md hover:bg-[#4b7443] focus:outline-none focus:shadow-outline-blue disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isResending ? 'Resending...' : 'Resend Verification Email'}
        </button>
      </div>
    </div>
  );
};

export default Verification;
